import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import {
  Copy,
  Check,
  Share2,
  TrendingUp,
  Users,
  Wallet,
  Clock,
  Banknote,
  ArrowDownToLine,
  ChevronRight,
  Download,
  QrCode,
  X,
} from "lucide-react";
import {
  getReferralSummary,
  listMyPayoutRequests,
  createPayoutRequest,
  checkReferralAvailability,
  setMyReferralCode,
} from "../api/referralApi";
import { formatApiError } from "../api/client";

/**
 * Share & Earn — Phase F.
 *
 * Auth required. NOT paywalled — free / expired-trial users must reach
 * this page (it's how they recover Pro for free via inviting friends).
 *
 * Visual language follows the rest of the app (zinc-950 base, emerald
 * accent, mono brand tone). All German UI strings per product spec.
 */

const eur = (n) => `${(Number(n) || 0).toFixed(2).replace(".", ",")} €`;

// V4.2 P0 — Custom slug availability check debounce.
const AVAILABILITY_DEBOUNCE_MS = 350;

// V4.2 P0 — QR code card (moved from the now-deleted
// ProfileReferralSection so /share-and-earn becomes the ONE canonical
// referral surface). SVG for crisp scaling + a hidden Canvas mirror
// for PNG download via canvas.toDataURL('image/png').
function ReferralQRCard({ url, code }) {
  const qrCanvasRef = useRef(null);
  const onDownload = useCallback(() => {
    const canvas = qrCanvasRef.current?.querySelector("canvas");
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `dealalert-referral-${code || "qr"}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [code]);
  return (
    <div
      data-testid="referral-qr-card"
      className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5 flex flex-col items-center gap-3"
    >
      <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 flex items-center gap-2">
        <QrCode size={14} strokeWidth={1.8} aria-hidden="true" />
        <span>QR-Code</span>
      </div>
      <div
        data-testid="referral-qr-svg"
        className="rounded-md border border-zinc-800 bg-white p-3"
        aria-label="QR-Code für deinen Empfehlungs-Link"
      >
        <QRCodeSVG value={url} size={160} bgColor="#FFFFFF" fgColor="#0c0c0e" includeMargin={false} />
      </div>
      {/* Hidden canvas mirror for PNG export. */}
      <div ref={qrCanvasRef} style={{ position: "absolute", left: "-9999px", top: "-9999px" }} aria-hidden="true">
        <QRCodeCanvas value={url} size={512} bgColor="#FFFFFF" fgColor="#0c0c0e" includeMargin={true} />
      </div>
      <button
        type="button"
        onClick={onDownload}
        data-testid="referral-qr-download-btn"
        className="inline-flex items-center gap-2 rounded-md border border-zinc-700 px-3 py-1.5 text-xs text-zinc-200 transition-colors hover:bg-zinc-800"
        title="QR als PNG herunterladen"
      >
        <Download size={13} aria-hidden="true" />
        Als PNG herunterladen
      </button>
      <div className="text-[11px] text-zinc-500 text-center max-w-[240px]">
        Zeig den Code auf dem Handy — Kamera scannt, Empfehlung ist gesetzt.
      </div>
    </div>
  );
}

// V4.2 P0 — Manual referral-code claim (moved from ProfileReferralSection).
// Debounced live availability check, save via setMyReferralCode.
function ReferralCustomSlug() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");
  const [check, setCheck] = useState({ state: "idle" });
  const checkAbortRef = useRef(null);
  const checkTimerRef = useRef(null);

  useEffect(() => {
    if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
    if (checkAbortRef.current) checkAbortRef.current.abort?.();
    const v = draft.trim();
    if (!v || v.length < 3) {
      setCheck({ state: "idle" });
      return;
    }
    setCheck({ state: "checking" });
    checkTimerRef.current = setTimeout(async () => {
      try {
        const r = await checkReferralAvailability(v);
        if (r.available) setCheck({ state: "available", normalized: r.normalized });
        else if (r.reason === "taken") setCheck({ state: "taken", normalized: r.normalized, message: r.message });
        else setCheck({ state: "invalid", normalized: r.normalized, message: r.message });
      } catch {
        setCheck({ state: "invalid", message: "Ungültig" });
      }
    }, AVAILABILITY_DEBOUNCE_MS);
    return () => {
      if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
    };
  }, [draft]);

  const mutation = useMutation({
    mutationFn: () => setMyReferralCode(draft.trim()),
    onSuccess: () => {
      toast.success("Empfehlungs-Code gespeichert");
      setDraft("");
      setCheck({ state: "idle" });
      qc.invalidateQueries({ queryKey: ["referrals"] });
    },
    onError: (err) => {
      const msg = err?.response?.data?.error?.message || "Konnte nicht gespeichert werden";
      toast.error(msg);
    },
  });

  const canSave = check.state === "available" && !mutation.isPending;
  const statusPill = (() => {
    switch (check.state) {
      case "checking":
        return { txt: "Prüfe …", cls: "text-zinc-400 border-zinc-700" };
      case "available":
        return { txt: "Verfügbar", cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" };
      case "taken":
        return { txt: "Vergeben", cls: "text-rose-300 border-rose-500/40 bg-rose-500/10" };
      case "invalid":
        return { txt: check.message || "Ungültig", cls: "text-amber-300 border-amber-500/40 bg-amber-500/10" };
      default:
        return null;
    }
  })();
  return (
    <div
      data-testid="referral-custom-slug-card"
      className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5"
    >
      <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">
        Eigener Empfehlungs-Code
      </div>
      <div className="mt-1 text-xs text-zinc-500">
        3–15 Zeichen · A–Z, 0–9 · muss verfügbar sein.
      </div>
      <div className="mt-3 flex flex-wrap gap-2 sm:flex-nowrap">
        <input
          type="text"
          inputMode="latin"
          maxLength={15}
          value={draft}
          onChange={(e) => setDraft(e.target.value.toUpperCase().replace(/\s+/g, ""))}
          placeholder="z.B. MAXI2026"
          data-testid="referral-custom-input"
          className="flex-1 min-w-0 rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 font-mono text-sm uppercase tracking-wider text-zinc-100 placeholder-zinc-600 focus:border-emerald-500 focus:outline-none"
        />
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={!canSave}
          data-testid="referral-custom-save-btn"
          className="inline-flex items-center gap-1 rounded-md bg-emerald-500 px-4 py-2 text-sm font-medium text-zinc-950 transition-colors hover:bg-emerald-400 disabled:cursor-not-allowed disabled:bg-zinc-700 disabled:text-zinc-400"
        >
          {mutation.isPending ? "Speichere …" : "Speichern"}
          <ChevronRight size={14} />
        </button>
      </div>
      {statusPill && (
        <div className="mt-2">
          <span
            data-testid={`referral-custom-status-${check.state}`}
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] uppercase tracking-wider ${statusPill.cls}`}
          >
            {statusPill.txt}
          </span>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, hint, accent = "zinc", testId }) {
  const accentRing = {
    zinc: "border-zinc-800",
    emerald: "border-emerald-500/30",
    amber: "border-amber-500/30",
    rose: "border-rose-500/20",
  }[accent];
  const accentTxt = {
    zinc: "text-zinc-100",
    emerald: "text-emerald-300",
    amber: "text-amber-300",
    rose: "text-rose-300",
  }[accent];
  return (
    <div
      data-testid={testId}
      className={`rounded-xl border ${accentRing} bg-zinc-900/40 p-5`}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-zinc-500">
        <Icon size={14} strokeWidth={1.8} aria-hidden="true" />
        <span>{label}</span>
      </div>
      <div className={`mt-2 text-2xl font-semibold ${accentTxt}`}>{value}</div>
      {hint && <div className="mt-1 text-xs text-zinc-500">{hint}</div>}
    </div>
  );
}

function CopyLinkCard({ url, code }) {
  const { t } = useTranslation("shareEarn");
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t("page.toast_link_copied"), { duration: 1500 });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t("page.toast_copy_failed"));
    }
  };

  // German share message — non-breaking newlines via %0A.
  const waText = encodeURIComponent(t("link_card.share_message") + url);

  return (
    <div
      data-testid="referral-link-card"
      className="rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 via-zinc-900/40 to-zinc-900/40 p-6"
    >
      <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.18em] text-emerald-300/80">
        <Share2 size={14} strokeWidth={1.8} aria-hidden="true" />
        <span>{t("link_card.label")}</span>
      </div>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <code
          data-testid="referral-url"
          className="flex-1 truncate rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-2 font-mono text-sm text-zinc-200"
        >
          {url}
        </code>
        <button
          type="button"
          onClick={onCopy}
          data-testid="referral-copy-btn"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-emerald-500 px-4 py-2 text-sm font-medium text-zinc-950 transition-colors hover:bg-emerald-400"
        >
          {copied ? <Check size={16} /> : <Copy size={16} />}
          {copied ? t("link_card.copied") : t("link_card.copy")}
        </button>
        <a
          href={`https://wa.me/?text=${waText}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="referral-whatsapp-btn"
          className="inline-flex items-center justify-center gap-2 rounded-md border border-zinc-700 px-4 py-2 text-sm text-zinc-200 transition-colors hover:bg-zinc-800"
        >
          <Share2 size={14} />
          {t("link_card.whatsapp")}
        </a>
      </div>
      <div className="mt-2 text-xs text-zinc-500">
        {t("link_card.code_prefix")} <span className="font-mono text-zinc-300">{code}</span>
      </div>
    </div>
  );
}

function HowItWorks() {
  const { t } = useTranslation("shareEarn");
  const steps = [
    { n: 1, title: t("how.step1_title"), desc: t("how.step1_desc") },
    { n: 2, title: t("how.step2_title"), desc: t("how.step2_desc") },
    { n: 3, title: t("how.step3_title"), desc: t("how.step3_desc") },
  ];
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
      <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">{t("how.label")}</div>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        {steps.map((s) => (
          <div key={s.n} className="rounded-lg border border-zinc-800/80 bg-zinc-950/40 p-4">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500/20 text-sm font-semibold text-emerald-300">
              {s.n}
            </div>
            <div className="mt-3 text-sm font-medium text-zinc-100">{s.title}</div>
            <div className="mt-1 text-xs text-zinc-400">{s.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ConditionsText({ holdingDays }) {
  return (
    <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-5 text-xs leading-relaxed text-zinc-400">
      <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Konditionen</div>
      <ul className="mt-3 space-y-1.5">
        <li>• <span className="text-zinc-200">2 €/Monat</span> Provision für Level 1 (direkter Empfehlung) — solange der Empfohlene zahlt.</li>
        <li>• <span className="text-zinc-200">1 €/Monat</span> Provision für Level 2 (wenn dein Empfohlener seinerseits jemanden empfiehlt, der zahlt).</li>
        <li>• Maximal <span className="text-zinc-200">3 €/Monat</span> Provision pro zahlendem Empfohlenem (kein Level 3+).</li>
        <li>• Provision nur auf <span className="text-zinc-200">erfolgreich gezahlte Rechnungen</span>. Während der Trial-Phase: 0 €.</li>
        <li>• Bei Rückerstattung oder Chargeback wird die Provision <span className="text-zinc-200">automatisch storniert</span>.</li>
        <li>• Auszahlungen ab <span className="text-zinc-200">25 €</span> möglich — manuell per SEPA-Banküberweisung nach Admin-Freigabe.</li>
        <li>• Sicherheitsfrist: <span className="text-zinc-200">{holdingDays} Tage</span> bevor eine Provision auszahlbar wird (Refund-Window).</li>
      </ul>
    </div>
  );
}

function PayoutRequestModal({ open, onClose, payableEur, onSubmit, busy }) {
  const [iban, setIban] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setIban("");
      setName("");
      setNote("");
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="payout-request-modal"
    >
      <button
        type="button"
        aria-label="Schließen"
        onClick={onClose}
        className="absolute inset-0 bg-zinc-950/80 backdrop-blur-sm"
      />
      <div className="relative w-full max-w-md rounded-xl border border-zinc-800 bg-zinc-950 p-6 shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          aria-label="Schließen"
          data-testid="payout-modal-close"
          className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-md border border-zinc-800 text-zinc-400 hover:bg-zinc-900"
        >
          <X size={14} />
        </button>
        <h2 className="text-lg font-semibold text-zinc-100">Auszahlung anfordern</h2>
        <p className="mt-1 text-sm text-zinc-400">
          Aktuell auszahlbar: <span className="font-semibold text-emerald-300">{eur(payableEur)}</span>
        </p>

        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setError(null);
            try {
              await onSubmit({ iban: iban.trim(), account_holder_name: name.trim(), note: note.trim() || null });
            } catch (err) {
              setError(formatApiError(err));
            }
          }}
          className="mt-5 space-y-4"
        >
          <div className="space-y-1.5">
            <label htmlFor="payout-iban" className="text-xs uppercase tracking-[0.18em] text-zinc-500">
              IBAN
            </label>
            <input
              id="payout-iban"
              type="text"
              required
              value={iban}
              onChange={(e) => setIban(e.target.value)}
              placeholder="AT00 0000 0000 0000 0000"
              data-testid="payout-iban-input"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 font-mono text-sm uppercase tracking-wider text-zinc-100 placeholder-zinc-600 focus:border-emerald-500 focus:outline-none"
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="payout-name" className="text-xs uppercase tracking-[0.18em] text-zinc-500">
              Kontoinhaber
            </label>
            <input
              id="payout-name"
              type="text"
              required
              minLength={2}
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Max Mustermann"
              data-testid="payout-name-input"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 focus:border-emerald-500 focus:outline-none"
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="payout-note" className="text-xs uppercase tracking-[0.18em] text-zinc-500">
              Notiz <span className="text-zinc-600">(optional)</span>
            </label>
            <textarea
              id="payout-note"
              rows={2}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="payout-note-input"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 focus:border-emerald-500 focus:outline-none"
            />
          </div>
          {error && (
            <div
              data-testid="payout-error"
              className="rounded-md border border-rose-900 bg-rose-950/40 px-3 py-2 text-sm text-rose-300"
            >
              {error}
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-md border border-zinc-700 px-4 py-2 text-sm text-zinc-200 hover:bg-zinc-900"
              data-testid="payout-cancel-btn"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="payout-submit-btn"
              className="flex-1 rounded-md bg-emerald-500 px-4 py-2 text-sm font-medium text-zinc-950 transition-colors hover:bg-emerald-400 disabled:opacity-60"
            >
              {busy ? "Wird gesendet…" : "Auszahlung anfordern"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { txt: "Ausstehend", cls: "border-amber-500/30 text-amber-300 bg-amber-500/10" },
    approved: { txt: "Genehmigt", cls: "border-sky-500/30 text-sky-300 bg-sky-500/10" },
    paid: { txt: "Ausgezahlt", cls: "border-emerald-500/30 text-emerald-300 bg-emerald-500/10" },
    rejected: { txt: "Abgelehnt", cls: "border-rose-500/30 text-rose-300 bg-rose-500/10" },
  };
  const v = map[status] || map.pending;
  return (
    <span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${v.cls}`}>
      {v.txt}
    </span>
  );
}

function PayoutHistory({ items }) {
  if (!items || items.length === 0) {
    return (
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/30 p-5 text-sm text-zinc-500">
        Noch keine Auszahlungsanfragen.
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/30">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-800 text-left text-xs uppercase tracking-[0.18em] text-zinc-500">
            <th className="px-4 py-3">Angefordert</th>
            <th className="px-4 py-3">Betrag</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Zeitpunkt</th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.ulid} className="border-b border-zinc-900/60 last:border-0" data-testid={`payout-row-${p.ulid}`}>
              <td className="px-4 py-3 text-zinc-300">
                {p.requested_at ? new Date(p.requested_at).toLocaleString("de-DE") : "—"}
              </td>
              <td className="px-4 py-3 font-medium text-zinc-100">{eur(p.requested_eur)}</td>
              <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
              <td className="px-4 py-3 text-xs text-zinc-500">
                {p.paid_at && `Bezahlt: ${new Date(p.paid_at).toLocaleDateString("de-DE")}`}
                {p.rejected_at && `Abgelehnt: ${new Date(p.rejected_at).toLocaleDateString("de-DE")}`}
                {p.approved_at && !p.paid_at && !p.rejected_at && `Genehmigt: ${new Date(p.approved_at).toLocaleDateString("de-DE")}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InvitedPreview({ items }) {
  if (!items || items.length === 0) {
    return (
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/30 p-5 text-sm text-zinc-500">
        Noch keine eingeladenen Nutzer.
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/30">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-800 text-left text-xs uppercase tracking-[0.18em] text-zinc-500">
            <th className="px-4 py-3">Email</th>
            <th className="px-4 py-3">Beigetreten</th>
            <th className="px-4 py-3">Zahlt?</th>
          </tr>
        </thead>
        <tbody>
          {items.map((u, i) => (
            <tr key={i} className="border-b border-zinc-900/60 last:border-0">
              <td className="px-4 py-3 font-mono text-xs text-zinc-300">{u.email_masked}</td>
              <td className="px-4 py-3 text-xs text-zinc-400">
                {u.registered_at ? new Date(u.registered_at).toLocaleDateString("de-DE") : "—"}
              </td>
              <td className="px-4 py-3">
                {u.is_paying ? (
                  <span className="inline-block rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-emerald-300">
                    Ja
                  </span>
                ) : (
                  <span className="text-xs text-zinc-500">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ShareAndEarnPage() {
  const { t } = useTranslation("shareEarn");
  const qc = useQueryClient();
  const [payoutOpen, setPayoutOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["referrals", "me"],
    queryFn: getReferralSummary,
  });
  const { data: payouts } = useQuery({
    queryKey: ["referrals", "payouts", "me"],
    queryFn: listMyPayoutRequests,
  });
  const mutation = useMutation({
    mutationFn: createPayoutRequest,
    onSuccess: () => {
      toast.success(t("page.toast_payout_sent"));
      qc.invalidateQueries({ queryKey: ["referrals"] });
      setPayoutOpen(false);
    },
  });

  const stats = data?.stats || {};
  const threshold = data?.payout_threshold_eur || 25;
  const canRequest = Boolean(data?.can_request_payout);
  const holdingDays = data?.holding_period_days || 14;

  const progress = useMemo(() => {
    const v = Math.min(100, ((stats.payable_eur || 0) / threshold) * 100);
    return Math.max(0, v);
  }, [stats.payable_eur, threshold]);

  if (isLoading) {
    return (
      <div className="py-12 text-center text-sm text-zinc-500" data-testid="share-earn-loading">
        {t("page.loading")}
      </div>
    );
  }
  if (error) {
    return (
      <div
        className="rounded-md border border-rose-900 bg-rose-950/40 px-4 py-3 text-sm text-rose-300"
        data-testid="share-earn-error"
      >
        {formatApiError(error)}
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="share-and-earn-page">
      {/* HERO */}
      <header className="space-y-2">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-emerald-400/80">
          <Share2 size={12} strokeWidth={2} />
          <span>{t("hero.eyebrow")}</span>
        </div>
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
          {t("hero.heading")}
        </h1>
        <p className="max-w-2xl text-sm text-zinc-400 sm:text-base">
          {t("hero.subline_a")} <span className="text-emerald-300">{t("hero.subline_l1")}</span>{" "}
          {t("hero.subline_b")} <span className="text-emerald-300">{t("hero.subline_l2")}</span>{" "}
          {t("hero.subline_c")}
        </p>
      </header>

      {/* Community Marketing Banner (Round-7) — sits above the link card */}
      <section
        data-testid="community-marketing-banner"
        className="rounded-2xl border border-amber-500/35 bg-amber-500/[0.08] p-5 sm:p-6"
      >
        <p className="text-base font-bold leading-snug text-amber-300 sm:text-lg">
          {t("community_banner.title")}
        </p>
        <p className="mt-2 text-sm text-zinc-400">
          {t("community_banner.sub")}
        </p>
      </section>

      {/* Link card */}
      <CopyLinkCard url={data.referral_url} code={data.referral_code} />

      {/* V4.2 P0 — QR code + custom slug moved here (canonical referral surface). */}
      <div className="grid gap-4 md:grid-cols-2">
        <ReferralQRCard url={data.referral_url} code={data.referral_code} />
        <ReferralCustomSlug />
      </div>

      <HowItWorks />

      {/* Stats grid */}
      <section className="space-y-3" data-testid="share-earn-stats">
        <h2 className="text-xs uppercase tracking-[0.18em] text-zinc-500">Deine Zahlen</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard
            icon={Users}
            label="Eingeladene Nutzer"
            value={stats.invited_users_count ?? 0}
            testId="stat-invited"
          />
          <StatCard
            icon={TrendingUp}
            label="Aktive zahlende Empfehlungen"
            value={stats.active_paid_referrals ?? 0}
            accent="emerald"
            testId="stat-active-paid"
          />
          <StatCard
            icon={Banknote}
            label="Geschätzte monatliche Einnahmen"
            value={eur(stats.estimated_monthly_eur)}
            accent="emerald"
            testId="stat-monthly"
          />
          <StatCard
            icon={Wallet}
            label="Auszahlbar"
            value={eur(stats.payable_eur)}
            hint={`Schwelle: ${eur(threshold)}`}
            accent="emerald"
            testId="stat-payable"
          />
          <StatCard
            icon={Clock}
            label={`Ausstehend (${holdingDays} Tage Sicherheitsfrist)`}
            value={eur(stats.pending_eur)}
            accent="amber"
            testId="stat-pending"
          />
          <StatCard
            icon={ArrowDownToLine}
            label="Bereits ausgezahlt"
            value={eur(stats.paid_eur)}
            testId="stat-paid"
          />
        </div>

        {/* Progress + payout CTA */}
        <div className="mt-4 rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Fortschritt bis zur nächsten Auszahlung</div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-zinc-800">
                <div
                  className="h-full bg-emerald-500 transition-all"
                  style={{ width: `${progress}%` }}
                  data-testid="payout-progress-bar"
                />
              </div>
              <div className="mt-1 text-xs text-zinc-500">
                {eur(stats.payable_eur)} von {eur(threshold)}
              </div>
            </div>
            <button
              type="button"
              disabled={!canRequest}
              onClick={() => setPayoutOpen(true)}
              data-testid="open-payout-modal-btn"
              className="rounded-md bg-emerald-500 px-5 py-2.5 text-sm font-medium text-zinc-950 transition-colors hover:bg-emerald-400 disabled:cursor-not-allowed disabled:bg-zinc-700 disabled:text-zinc-400"
              title={canRequest ? "Auszahlung anfordern" : `Mindestens ${eur(threshold)} auszahlbar erforderlich`}
            >
              Auszahlung anfordern
            </button>
          </div>
        </div>
      </section>

      {/* Invited preview */}
      <section className="space-y-3">
        <h2 className="text-xs uppercase tracking-[0.18em] text-zinc-500">Eingeladene Nutzer (letzte 10)</h2>
        <InvitedPreview items={data.invited_users_preview || []} />
      </section>

      {/* Payout history */}
      <section className="space-y-3">
        <h2 className="text-xs uppercase tracking-[0.18em] text-zinc-500">Auszahlungs-Historie</h2>
        <PayoutHistory items={payouts || []} />
      </section>

      <ConditionsText holdingDays={holdingDays} />

      <PayoutRequestModal
        open={payoutOpen}
        onClose={() => setPayoutOpen(false)}
        payableEur={stats.payable_eur || 0}
        onSubmit={(body) => mutation.mutateAsync(body)}
        busy={mutation.isPending}
      />
    </div>
  );
}
